import { Temporal } from '@js-temporal/polyfill';
import { logger } from './logger';
import { getEventDisplayZone } from './eventDisplayZone';

// ─── Locale setup ─────────────────────────────────────────────────────────────

let currentLocale: string = (() => {
  try {
    return navigator.language || 'en-US';
  } catch {
    return 'en-US'; // SSR / test environments without navigator
  }
})();

export const initializeLocale = () => {
  try {
    currentLocale = navigator.language || 'en-US';
  } catch (error) {
    logger.warn('Failed to detect browser locale, using default:', error);
    currentLocale = 'en-US';
  }
};

/** The locale every formatter here writes in, so a number elsewhere can be written the same way. */
export const getLocale = (): string => currentLocale;

// ─── Internal parse helper ────────────────────────────────────────────────────

const toDate = (input: Date | string): Date => {
  if (input instanceof Date) return input;
  try {
    const instant = Temporal.Instant.from(input);
    return new Date(instant.epochMilliseconds);
  } catch {
    try {
      const pdt = Temporal.PlainDateTime.from(input);
      return new Date(
        pdt.year,
        pdt.month - 1,
        pdt.day,
        pdt.hour,
        pdt.minute,
        pdt.second,
        pdt.millisecond,
      );
    } catch {
      return new Date(NaN);
    }
  }
};

// ─── Format helpers ───────────────────────────────────────────────────────────

const pad2 = (n: number) => String(n).padStart(2, '0');

const zdtOf = (d: Date) =>
  Temporal.Instant.from(d.toISOString()).toZonedDateTimeISO(Temporal.Now.timeZoneId());

// Intl.DateTimeFormat construction is expensive; cache per locale+options so
// list renders (event/question cards) don't allocate a formatter per row.
const formatterCache = new Map<string, Intl.DateTimeFormat>();
const getFormatter = (opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat => {
  const key = `${currentLocale}|${JSON.stringify(opts)}`;
  let f = formatterCache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(currentLocale, opts);
    formatterCache.set(key, f);
  }
  return f;
};

const intl = (d: Date, opts: Intl.DateTimeFormatOptions) => getFormatter(opts).format(d);

const intlInTz = (d: Date, opts: Intl.DateTimeFormatOptions, tz: string) =>
  getFormatter({ ...opts, timeZone: tz }).format(d);

const plainDateIn = (d: Date, timeZone: string) =>
  Temporal.Instant.from(d.toISOString()).toZonedDateTimeISO(timeZone).toPlainDate();

// ─── Public formatters ────────────────────────────────────────────────────────

export const formatDate = (date: Date | string, timeZone?: string): string => {
  const d = toDate(date);
  if (isNaN(d.getTime())) return '';
  const opts: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };
  return timeZone ? intlInTz(d, opts, timeZone) : intl(d, opts);
};

export const formatTime = (date: Date | string): string => {
  const d = toDate(date);
  if (isNaN(d.getTime())) return '';
  return intl(d, { hour: 'numeric', minute: '2-digit' });
};

export const formatDateTime = (date: Date | string): string => {
  const d = toDate(date);
  if (isNaN(d.getTime())) return '';
  return intl(d, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const relativeFormatterCache = new Map<string, Intl.RelativeTimeFormat>();
const getRelativeFormatter = (numeric: 'always' | 'auto'): Intl.RelativeTimeFormat => {
  const key = `${currentLocale}|${numeric}`;
  let f = relativeFormatterCache.get(key);
  if (!f) {
    f = new Intl.RelativeTimeFormat(currentLocale, { numeric });
    relativeFormatterCache.set(key, f);
  }
  return f;
};

/**
 * "2 hours ago" / "in 3 days", in the viewer's locale ("hace 2 horas").
 *
 * Localized like every other formatter here, as date-fns was before this module
 * replaced it. Intl has no "about" or "less than a minute", so under a minute
 * reads "now".
 */
export const formatDistanceToNow = (
  date: Date | string,
  options?: { addSuffix?: boolean; includeSeconds?: boolean },
): string => {
  try {
    const d = toDate(date);
    if (isNaN(d.getTime())) return 'unknown time';
    const diffMs = Date.now() - d.getTime();
    const sign = diffMs >= 0 ? -1 : 1;
    const absMs = Math.abs(diffMs);
    const absSeconds = Math.floor(absMs / 1000);
    const absMinutes = Math.floor(absSeconds / 60);
    const absHours = Math.floor(absMinutes / 60);
    const absDays = Math.floor(absHours / 24);
    const absMonths = Math.floor(absDays / 30.44);
    const absYears = Math.floor(absDays / 365.25);

    const [value, unit]: [number, Intl.RelativeTimeFormatUnit] =
      absYears >= 1
        ? [absYears, 'year']
        : absMonths >= 1
          ? [absMonths, 'month']
          : absDays >= 1
            ? [absDays, 'day']
            : absHours >= 1
              ? [absHours, 'hour']
              : [absMinutes, 'minute'];

    if (options?.addSuffix === false) {
      return new Intl.NumberFormat(currentLocale, {
        style: 'unit',
        unit,
        unitDisplay: 'long',
      }).format(value);
    }
    if (value === 0) return getRelativeFormatter('auto').format(0, 'second');
    return getRelativeFormatter('always').format(sign * value, unit);
  } catch (error) {
    logger.warn('Error formatting date distance:', error, date);
    return 'unknown time';
  }
};

export const formatCompactTimeAgo = (date: Date | string): string => {
  try {
    const d = toDate(date);
    if (isNaN(d.getTime())) return '';
    const now = Temporal.Now.instant();
    const then = Temporal.Instant.from(d.toISOString());
    const diffSeconds = Math.floor(
      now.since(then, { smallestUnit: 'seconds' }).total({ unit: 'seconds' }),
    );
    if (diffSeconds < 60) return 'now';
    const diffMinutes = Math.floor(diffSeconds / 60);
    if (diffMinutes < 60) return `${diffMinutes}m`;
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d`;
    const diffWeeks = Math.floor(diffDays / 7);
    if (diffDays < 31) return `${diffWeeks}w`;
    const diffMonths = Math.floor(diffDays / 30.44);
    if (diffMonths < 12) return `${diffMonths}mo`;
    return `${Math.floor(diffDays / 365.25)}y`;
  } catch (error) {
    logger.warn('Error formatting compact time ago:', error, date);
    return '';
  }
};

// ─── Event date and time in the display zone ─────────────────────────────────

export interface EventWhenInput {
  start_date: string;
  end_date?: string | null;
  location?: string | null;
  timezone?: string | null;
}

export interface EventWhenParts {
  /** "Today" / "Tomorrow" / "Tue 16 Jun" (locale-ordered), in the display zone. */
  date: string;
  /** "19:00 - 21:00" / "19:00" / "19:00 · ends 18 Jun", in the display zone. */
  time: string;
  /**
   * The viewer's START time, and their short weekday when it falls on a different
   * calendar day from the event-local start. `null` when the event is shown in the
   * viewer's own zone or both clocks read the same.
   */
  viewer: { time: string; day: string | null } | null;
}

const EVENT_TIME: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' };

/**
 * An event's date and time, framed per the caller's question (docs/date-formatting/design.md,
 * "Event lists use the viewer's timezone"):
 *
 * - `frame: 'event'` (default) — "how do I show up?" In-person events render in the venue's
 *   zone (getEventDisplayZone) plus the viewer's start time in brackets; online and legacy
 *   events render in the viewer's zone with no bracket. This is the event PAGE's frame.
 * - `frame: 'viewer'` — "what's coming up for me?" Everything renders in the viewer's own
 *   zone; `viewer` is always `null` (there is nothing left to bracket). This is the LIST
 *   frame (cards, headers, chips, pickers).
 *
 * Everything is formatted with an explicit `timeZone`, so the day maths and the Intl output
 * can never disagree.
 *
 * `now` anchors "Today"/"Tomorrow" (in the display zone); pass the host's clock where one
 * exists.
 */
export const formatEventWhen = (
  event: EventWhenInput,
  now: Date = new Date(),
  frame: 'event' | 'viewer' = 'event',
): EventWhenParts => {
  const start = toDate(event.start_date);
  if (isNaN(start.getTime())) return { date: '', time: '', viewer: null };

  const viewerZone = getBrowserTimezone();
  const zone = frame === 'viewer' ? viewerZone : getEventDisplayZone(event);
  const startDay = plainDateIn(start, zone);
  const today = plainDateIn(now, zone);

  let date: string;
  if (Temporal.PlainDate.compare(startDay, today) === 0) date = 'Today';
  else if (Temporal.PlainDate.compare(startDay, today.add({ days: 1 })) === 0) date = 'Tomorrow';
  else date = intlInTz(start, { weekday: 'short', day: 'numeric', month: 'short' }, zone);

  const startTime = intlInTz(start, EVENT_TIME, zone);
  let time = startTime;
  const end = event.end_date ? toDate(event.end_date) : null;
  if (end && !isNaN(end.getTime())) {
    time =
      Temporal.PlainDate.compare(plainDateIn(end, zone), startDay) === 0
        ? `${startTime} - ${intlInTz(end, EVENT_TIME, zone)}`
        : `${startTime} · ends ${intlInTz(end, { day: 'numeric', month: 'short' }, zone)}`;
  }

  let viewer: EventWhenParts['viewer'] = null;
  if (frame === 'event' && zone !== viewerZone) {
    const viewerTime = intlInTz(start, EVENT_TIME, viewerZone);
    const otherDay = Temporal.PlainDate.compare(plainDateIn(start, viewerZone), startDay) !== 0;
    if (viewerTime !== startTime || otherDay) {
      viewer = {
        time: viewerTime,
        day: otherDay ? intlInTz(start, { weekday: 'short' }, viewerZone) : null,
      };
    }
  }

  return { date, time, viewer };
};

// ─── New display formatters ───────────────────────────────────────────────────

export const formatEventDetailDate = (start: Date | string, end?: Date | string | null): string => {
  const startDate = toDate(start);
  if (isNaN(startDate.getTime())) return '';
  const zdt = zdtOf(startDate);
  const startFormatted = intl(startDate, {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  if (!end) return startFormatted;
  const endDate = toDate(end);
  if (isNaN(endDate.getTime())) return startFormatted;
  if (formatLocalDateISO(startDate) === formatLocalDateISO(endDate)) return startFormatted;
  const endZdt = zdtOf(endDate);
  if (zdt.year === endZdt.year) {
    return `${intl(startDate, { day: 'numeric', month: 'short' })} – ${intl(endDate, { day: 'numeric', month: 'short', year: 'numeric' })}`;
  }
  return `${intl(startDate, { day: 'numeric', month: 'short', year: 'numeric' })} – ${intl(endDate, { day: 'numeric', month: 'short', year: 'numeric' })}`;
};

export const formatEventDetailTimeRange = (
  start: Date | string,
  end?: Date | string | null,
): string => {
  const startDate = toDate(start);
  if (isNaN(startDate.getTime())) return '';
  if (!end) return formatTime(startDate);
  const endDate = toDate(end);
  if (isNaN(endDate.getTime())) return formatTime(startDate);
  return `${formatTime(startDate)} – ${formatTime(endDate)}`;
};

export const formatMemberSince = (date: Date | string): string => {
  const d = toDate(date);
  if (isNaN(d.getTime())) return '';
  return intl(d, { month: 'short', year: 'numeric' });
};

export const formatBlockedDate = (date: Date | string): string => {
  const d = toDate(date);
  if (isNaN(d.getTime())) return '';
  return intl(d, { day: 'numeric', month: 'short', year: 'numeric' });
};

/** "Sat, Jun 14" — date picker / range step display */
export const formatPickerDate = (date: Date | string): string => {
  const d = toDate(date);
  if (isNaN(d.getTime())) return '';
  return intl(d, { weekday: 'short', month: 'short', day: 'numeric' });
};

/** "Saturday, June 14" — create-event summary line */
export const formatEventSummaryDate = (date: Date | string): string => {
  const d = toDate(date);
  if (isNaN(d.getTime())) return '';
  return intl(d, { weekday: 'long', month: 'long', day: 'numeric' });
};

/** "Jun 14, 2026" — interaction cards, crosspost modal */
export const formatShortDate = (date: Date | string, timeZone?: string): string => {
  const d = toDate(date);
  if (isNaN(d.getTime())) return '';
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: '2-digit', year: 'numeric' };
  return timeZone ? intlInTz(d, opts, timeZone) : intl(d, opts);
};

/** "June 14, 2026" — chat date separators */
export const formatChatDate = (date: Date | string): string => {
  const d = toDate(date);
  if (isNaN(d.getTime())) return '';
  return intl(d, { month: 'long', day: '2-digit', year: 'numeric' });
};

/** "Wednesday" — event timeline date-group headers */
export const formatDayName = (date: Date | string, timeZone?: string): string => {
  const d = toDate(date);
  if (isNaN(d.getTime())) return '';
  return timeZone ? intlInTz(d, { weekday: 'long' }, timeZone) : intl(d, { weekday: 'long' });
};

/**
 * "Today" / "Tomorrow" / "Jun 14" / "Jun 14, 2026" (year only when it differs
 * from the current year) — event timeline date-group headers.
 *
 * With `timeZone`, the day and "today" are both read in that zone, so a header
 * agrees with the event-local dates of the cards under it.
 */
export const formatCalendarLabel = (date: Date | string, timeZone?: string): string => {
  const d = toDate(date);
  if (isNaN(d.getTime())) return '';
  if (!timeZone) {
    if (isDateToday(d)) return 'Today';
    if (isDateTomorrow(d)) return 'Tomorrow';
    const sameYear = d.getFullYear() === new Date().getFullYear();
    return intl(d, { month: 'short', day: 'numeric', year: sameYear ? undefined : 'numeric' });
  }
  const day = plainDateIn(d, timeZone);
  const today = plainDateIn(new Date(), timeZone);
  if (Temporal.PlainDate.compare(day, today) === 0) return 'Today';
  if (Temporal.PlainDate.compare(day, today.add({ days: 1 })) === 0) return 'Tomorrow';
  return intlInTz(
    d,
    { month: 'short', day: 'numeric', year: day.year === today.year ? undefined : 'numeric' },
    timeZone,
  );
};

/** "9:30 AM" — time picker display, create-event time summary */
export const format12HourTime = (date: Date | string): string => {
  const d = toDate(date);
  if (isNaN(d.getTime())) return '';
  return intl(d, { hour: 'numeric', minute: '2-digit', hour12: true });
};

// ─── Computation functions ────────────────────────────────────────────────────

export const getEventDurationHours = (start: Date | string, end: Date | string): number => {
  try {
    const s = Temporal.Instant.from(toDate(start).toISOString());
    const e = Temporal.Instant.from(toDate(end).toISOString());
    return Math.round(e.since(s, { smallestUnit: 'seconds' }).total({ unit: 'hours' }));
  } catch {
    return 0;
  }
};

export const getEventDurationDays = (start: Date | string, end: Date | string): number => {
  try {
    const s = Temporal.Instant.from(toDate(start).toISOString());
    const e = Temporal.Instant.from(toDate(end).toISOString());
    return Math.round(e.since(s, { smallestUnit: 'seconds' }).total({ unit: 'days' }));
  } catch {
    return 0;
  }
};

export const getMinutesDifference = (a: Date | string, b: Date | string): number => {
  try {
    const ia = Temporal.Instant.from(toDate(a).toISOString());
    const ib = Temporal.Instant.from(toDate(b).toISOString());
    return Math.floor(ia.since(ib, { smallestUnit: 'seconds' }).total({ unit: 'minutes' }));
  } catch {
    return 0;
  }
};

export const isDateToday = (date: Date | string): boolean => {
  try {
    const d = toDate(date);
    if (isNaN(d.getTime())) return false;
    const tz = Temporal.Now.timeZoneId();
    const localDate = Temporal.Instant.from(d.toISOString()).toZonedDateTimeISO(tz).toPlainDate();
    return Temporal.PlainDate.compare(Temporal.Now.plainDateISO(), localDate) === 0;
  } catch {
    return false;
  }
};

export const isDateYesterday = (date: Date | string): boolean => {
  try {
    const d = toDate(date);
    if (isNaN(d.getTime())) return false;
    const tz = Temporal.Now.timeZoneId();
    const localDate = Temporal.Instant.from(d.toISOString()).toZonedDateTimeISO(tz).toPlainDate();
    const yesterday = Temporal.Now.plainDateISO().subtract({ days: 1 });
    return Temporal.PlainDate.compare(yesterday, localDate) === 0;
  } catch {
    return false;
  }
};

export const isDateTomorrow = (date: Date | string): boolean => {
  try {
    const d = toDate(date);
    if (isNaN(d.getTime())) return false;
    const tz = Temporal.Now.timeZoneId();
    const localDate = Temporal.Instant.from(d.toISOString()).toZonedDateTimeISO(tz).toPlainDate();
    const tomorrow = Temporal.Now.plainDateISO().add({ days: 1 });
    return Temporal.PlainDate.compare(tomorrow, localDate) === 0;
  } catch {
    return false;
  }
};

export const isSameDateDay = (a: Date | string, b: Date | string): boolean => {
  try {
    const da = toDate(a);
    const db = toDate(b);
    if (isNaN(da.getTime()) || isNaN(db.getTime())) return false;
    const tz = Temporal.Now.timeZoneId();
    const pdA = Temporal.Instant.from(da.toISOString()).toZonedDateTimeISO(tz).toPlainDate();
    const pdB = Temporal.Instant.from(db.toISOString()).toZonedDateTimeISO(tz).toPlainDate();
    return Temporal.PlainDate.compare(pdA, pdB) === 0;
  } catch {
    return false;
  }
};

export const parseEventDate = (iso: string): Date | null => {
  try {
    const d = toDate(iso);
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
};

export const formatLocalDateISO = (date: Date): string => {
  if (isNaN(date.getTime())) return '';
  const tz = Temporal.Now.timeZoneId();
  const zdt = Temporal.Instant.from(date.toISOString()).toZonedDateTimeISO(tz);
  return `${zdt.year}-${pad2(zdt.month)}-${pad2(zdt.day)}`;
};

/** "YYYY-MM-DD" for the calendar day of `date` in `timeZone`; '' when unparseable. */
export const formatDateKey = (date: Date | string, timeZone: string): string => {
  const d = toDate(date);
  if (isNaN(d.getTime())) return '';
  return plainDateIn(d, timeZone).toString();
};

// ─── Utility functions ────────────────────────────────────────────────────────

export const getBrowserTimezone = (): string => {
  try {
    return Temporal.Now.timeZoneId();
  } catch {
    return 'UTC';
  }
};

/**
 * The zone name the device's engine reports, read afresh on every call so a change of zone shows
 * on the next one; undefined when Intl throws or names no zone. Usually an IANA name, but engines
 * differ: V8 can give a legacy link name (`Asia/Calcutta`) or, for a host set to plain GMT, an
 * offset (`+00:00`). Unlike `getBrowserTimezone`, it never makes up 'UTC': its answer goes to the
 * backend as the member's own zone, where a guess would be stored as a fact.
 */
export const readDeviceTimezone = (): string | undefined => {
  try {
    return new Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
};

export const getOffsetMinutes = (tz: string, at: Date = new Date()): number => {
  try {
    const zdt = Temporal.Instant.from(at.toISOString()).toZonedDateTimeISO(tz);
    return zdt.offsetNanoseconds / (60 * 1_000_000_000);
  } catch (e) {
    logger.warn('getOffsetMinutes: invalid timezone', tz, e);
    return 0;
  }
};

const NAIVE_WALL_CLOCK = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;

export const getDefaultEventDates = (now: Date = new Date()): { start: string; end: string } => {
  const tz = Temporal.Now.timeZoneId();
  const zdt = Temporal.Instant.from(now.toISOString()).toZonedDateTimeISO(tz);
  const fmt = (z: Temporal.ZonedDateTime) =>
    `${z.year}-${pad2(z.month)}-${pad2(z.day)}T${pad2(z.hour)}:${pad2(z.minute)}`;
  // The wall clock we return drops the offset, and toEventInstant resolves a
  // repeated wall time to its EARLIER occurrence. At a DST fall-back, "next hour,
  // floored" can land on the second 01:00, which reads back as the first — before
  // `now`. Skip any wall time that does not round-trip, and never default to <= now.
  const usable = (z: Temporal.ZonedDateTime) =>
    Temporal.ZonedDateTime.from(`${fmt(z)}[${tz}]`).epochNanoseconds === z.epochNanoseconds &&
    Temporal.Instant.compare(z.toInstant(), zdt.toInstant()) > 0;
  // Step in exact hours and do not re-round inside the loops: re-rounding after a
  // 30-minute shift (Australia/Lord_Howe) can land back on the same instant forever.
  let start = zdt.add({ hours: 1 }).round({ smallestUnit: 'hour', roundingMode: 'floor' });
  while (!usable(start)) start = start.add({ hours: 1 });
  let end = start.add({ hours: 2 });
  while (!usable(end)) end = end.add({ hours: 1 });
  return { start: fmt(start), end: fmt(end) };
};

export const toWallClock = (iso: string, tz?: string): string => {
  try {
    const instant = Temporal.Instant.from(iso);
    const preferredTz = tz ?? Temporal.Now.timeZoneId();
    let zdt: Temporal.ZonedDateTime;
    try {
      zdt = instant.toZonedDateTimeISO(preferredTz);
    } catch (e) {
      // Invalid timezone string (e.g. a malformed backend tz) — surface it so
      // the wrong-timezone render is visible in logs, then fall back to local.
      logger.warn('toWallClock: invalid timezone, falling back to browser-local', preferredTz, e);
      zdt = instant.toZonedDateTimeISO(Temporal.Now.timeZoneId());
    }
    return `${zdt.year}-${pad2(zdt.month)}-${pad2(zdt.day)}T${pad2(zdt.hour)}:${pad2(zdt.minute)}`;
  } catch {
    return '';
  }
};

export const isValidTimezone = (tz: string): boolean => {
  try {
    Temporal.Now.instant().toZonedDateTimeISO(tz);
    return true;
  } catch {
    return false;
  }
};

/**
 * Convert a naive wall-clock in `tz` to a UTC instant; an already-absolute
 * value passes through.
 *
 * Throws a RangeError when `tz` cannot be resolved. Returning the naive value
 * instead would put an unzoned `YYYY-MM-DDTHH:mm` on the wire, which the backend
 * accepts and stores as UTC, silently moving the event by the sender's offset.
 * Callers validate first (`isValidTimezone`) so the user gets a specific error.
 */
export const toEventInstant = (value: string, tz: string): string => {
  if (!NAIVE_WALL_CLOCK.test(value)) return value;

  let zdt: Temporal.ZonedDateTime;
  try {
    zdt = Temporal.ZonedDateTime.from(`${value}[${tz}]`);
  } catch (error) {
    throw new RangeError(
      `toEventInstant: cannot resolve ${JSON.stringify(value)} in ${JSON.stringify(tz)}`,
      {
        cause: error,
      },
    );
  }
  // Date.toISOString() produces the canonical .000Z format that the backend
  // and round-trip tests expect.
  return new Date(zdt.toInstant().epochMilliseconds).toISOString();
};

/** Whether two ISO datetime strings denote the same absolute moment. */
export const sameInstant = (a: string, b: string): boolean =>
  new Date(a).getTime() === new Date(b).getTime();
